<template>
  <div class="carga-ots-comp">
    <!-- ===================== RESUMEN DE LA COMPARATIVA ===================== -->
    <v-row class="pa-0 ma-0">
      <v-col cols="12" sm="6" md="3">
        <v-card class="fill-height d-flex">
          <v-card-text class="text-center align-self-center">
            <div class="subtitle-1 font-weight-bold">Piezas en el CSV</div>
            <div v-if="!cargando" class="text-h5 font-weight-medium">{{ fmtNum(piezasCsv) }}</div>
            <v-progress-circular v-else :size="25" color="primary" indeterminate />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="fill-height d-flex">
          <v-card-text class="text-center align-self-center">
            <div class="subtitle-1 font-weight-bold">Piezas programadas</div>
            <div v-if="!cargando" class="text-h5 font-weight-medium">{{ fmtNum(piezasProgramadas) }}</div>
            <v-progress-circular v-else :size="25" color="primary" indeterminate />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="fill-height d-flex">
          <v-card-text class="text-center align-self-center">
            <div class="subtitle-1 font-weight-bold">Diferencia de piezas</div>
            <div v-if="!cargando" class="text-h5 font-weight-medium" :class="claseDiferencia(diferencia)">
              {{ fmtDif(diferencia) }}
            </div>
            <v-progress-circular v-else :size="25" color="primary" indeterminate />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="fill-height d-flex">
          <v-card-text class="text-center align-self-center">
            <div class="subtitle-1 font-weight-bold">Órdenes con diferencia</div>
            <div v-if="!cargando" class="text-h5 font-weight-medium"
              :class="totalDiferencias > 0 ? 'red--text' : ''">
              {{ fmtNum(totalDiferencias) }}
            </div>
            <v-progress-circular v-else :size="25" color="primary" indeterminate />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense outlined class="mt-3">{{ error }}</v-alert>
    <v-alert v-else-if="mensaje" type="info" dense outlined class="mt-3">{{ mensaje }}</v-alert>

    <div class="barra mt-3">
      <v-text-field v-model="busqueda" dense outlined hide-details clearable label="Buscar"
        prepend-inner-icon="mdi-magnify" class="buscador" />
      <v-switch v-model="soloDiferencias" dense hide-details class="mt-0 pt-0 ml-2"
        label="Solo diferencias" />
      <v-spacer />
      <span v-if="auditoria" class="caption grey--text">
        Auditoría: {{ prop(auditoria, 'carpeta') }}
        <template v-if="fechaAuditoria"> · {{ fechaAuditoria }}</template>
      </span>
    </div>

    <v-data-table :headers="headers" :items="itemsFiltrados" :search="busqueda" :loading="cargando"
      :items-per-page="25" :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
      class="mt-2" dense
      loading-text="Cargando... Espere, Por Favor."
      :no-data-text="soloDiferencias
        ? 'Sin diferencias entre el CSV y las piezas programadas del periodo.'
        : 'No hay órdenes que comparar en este periodo.'">
      <template v-slot:item.Clave="{ item }">
        <span class="font-weight-medium">{{ item.Clave }}</span>
      </template>

      <template v-slot:item.FechaCompromisoEntrega="{ item }">
        {{ fmtFecha(item.FechaCompromisoEntrega) }}
      </template>

      <template v-slot:item.PiezasCsv="{ item }">
        {{ fmtNum(item.PiezasCsv) }}
      </template>

      <template v-slot:item.PiezasProgramadas="{ item }">
        {{ fmtNum(item.PiezasProgramadas) }}
      </template>

      <template v-slot:item.Diferencia="{ item }">
        <span :class="claseDiferencia(item.Diferencia)">{{ fmtDif(item.Diferencia) }}</span>
      </template>

      <template v-slot:item.Tipo="{ item }">
        <v-chip x-small :color="colorTipo(item.Tipo)" dark>{{ etiquetaTipo(item.Tipo) }}</v-chip>
      </template>

      <template v-slot:item.Detalle="{ item }">
        <span class="detalle">{{ item.Detalle }}</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import axios from 'axios'

// Lee una propiedad aceptando camelCase o PascalCase (el back serializa en PascalCase).
const prop = (obj, camel) => {
  if (!obj) return undefined
  if (obj[camel] !== undefined) return obj[camel]
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1)
  return obj[pascal]
}

// Tipos de diferencia que devuelve el back, con su etiqueta y color.
const TIPOS = {
  Duplicada: { etiqueta: 'Duplicada', color: 'deep-purple' },
  NoCargada: { etiqueta: 'No cargada', color: 'red' },
  SinPlan: { etiqueta: 'Sin plan', color: 'orange darken-2' },
  BajaEnCsv: { etiqueta: 'Baja (C) sin aplicar', color: 'brown' },
  SoloEnPlan: { etiqueta: 'Falta en el CSV', color: 'blue-grey' },
  MasPiezas: { etiqueta: 'Piezas de más', color: 'blue' },
  MenosPiezas: { etiqueta: 'Piezas de menos', color: 'deep-orange' },
  Ok: { etiqueta: 'Coincide', color: 'green' },
}

export default {
  name: 'CargaOtsComparativa',

  data() {
    return {
      cargando: false,
      error: '',
      mensaje: '',
      auditoria: null,
      ordenes: [],
      piezasCsv: 0,
      piezasProgramadas: 0,
      diferencia: 0,
      totalDiferencias: 0,
      busqueda: '',
      soloDiferencias: true,
    }
  },

  computed: {
    fechaAuditoria() {
      const f = prop(this.auditoria, 'fecha')
      return f ? this.fmtFecha(f) : ''
    },

    itemsFiltrados() {
      if (!this.soloDiferencias) return this.ordenes
      return this.ordenes.filter((o) => o.Tipo !== 'Ok')
    },

    headers() {
      return [
        { text: 'ODT', value: 'Clave' },
        { text: 'Fecha compromiso', value: 'FechaCompromisoEntrega' },
        { text: 'Máquina', value: 'Maquina' },
        { text: 'Molde', value: 'Molde' },
        { text: 'Piezas CSV', value: 'PiezasCsv', align: 'end' },
        { text: 'Programadas', value: 'PiezasProgramadas', align: 'end' },
        { text: 'Diferencia', value: 'Diferencia', align: 'end' },
        { text: 'Tipo', value: 'Tipo' },
        { text: 'Detalle', value: 'Detalle' },
      ]
    },
  },

  methods: {
    prop,

    fmtNum(n) {
      return Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })
    },

    // La diferencia lleva signo: +N piezas de más en el CSV, -N de menos.
    fmtDif(n) {
      const v = Number(n || 0)
      if (v === 0) return '0'
      return `${v > 0 ? '+' : '−'}${this.fmtNum(Math.abs(v))}`
    },

    claseDiferencia(n) {
      const v = Number(n || 0)
      if (v > 0) return 'blue--text'
      if (v < 0) return 'deep-orange--text'
      return ''
    },

    fmtFecha(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return '—'
      const p = (n) => String(n).padStart(2, '0')
      return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
    },

    etiquetaTipo(tipo) {
      return (TIPOS[tipo] && TIPOS[tipo].etiqueta) || tipo || '—'
    },

    colorTipo(tipo) {
      return (TIPOS[tipo] && TIPOS[tipo].color) || 'grey'
    },

    // Lo llama el contenedor con el rango vigente de los filtros.
    async cargar(inicio, fin) {
      this.cargando = true
      this.$emit('cargando', true)
      this.error = ''
      this.mensaje = ''
      try {
        const p = new URLSearchParams()
        if (inicio) p.append('inicio', inicio)
        if (fin) p.append('fin', fin)

        const { data } = await axios.get(`/CargaOts/Comparativa?${p.toString()}`)
        this.auditoria = prop(data, 'auditoria') || null
        this.ordenes = (prop(data, 'ordenes') || []).map((o) => this.normalizar(o))
        this.piezasCsv = Number(prop(data, 'piezasCsv')) || 0
        this.piezasProgramadas = Number(prop(data, 'piezasProgramadas')) || 0
        this.diferencia = Number(prop(data, 'diferencia')) || 0
        this.totalDiferencias = Number(prop(data, 'totalDiferencias')) || 0
        this.mensaje = prop(data, 'mensaje') || ''
      } catch (e) {
        this.ordenes = []
        this.auditoria = null
        this.piezasCsv = 0
        this.piezasProgramadas = 0
        this.diferencia = 0
        this.totalDiferencias = 0
        this.error = (e && e.response && e.response.data && e.response.data.message)
          || 'No se pudo generar la comparativa contra el plan.'
        // eslint-disable-next-line no-console
        console.error('Error generando la comparativa de la carga de OTs', e)
      } finally {
        this.cargando = false
        this.$emit('cargando', false)
      }
    },

    normalizar(o) {
      return {
        Clave: prop(o, 'clave'),
        ClavesCsv: prop(o, 'clavesCsv'),
        FechaCompromisoEntrega: prop(o, 'fechaCompromisoEntrega'),
        Maquina: prop(o, 'maquina'),
        Molde: prop(o, 'molde'),
        PiezasCsv: Number(prop(o, 'piezasCsv')) || 0,
        PiezasProgramadas: Number(prop(o, 'piezasProgramadas')) || 0,
        Diferencia: Number(prop(o, 'diferencia')) || 0,
        VecesEnCsv: Number(prop(o, 'vecesEnCsv')) || 0,
        IdTrabajoOrden: prop(o, 'idTrabajoOrden'),
        EnCsv: !!prop(o, 'enCsv'),
        EnPlan: !!prop(o, 'enPlan'),
        ExisteEnSistema: !!prop(o, 'existeEnSistema'),
        EsBaja: !!prop(o, 'esBaja'),
        Tipo: prop(o, 'tipo'),
        Detalle: prop(o, 'detalle'),
      }
    },
  },
}
</script>

<style scoped>
.barra {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.buscador {
  max-width: 320px;
}

.detalle {
  display: inline-block;
  min-width: 320px;
  white-space: normal;
  line-height: 1.3;
}
</style>
